import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import * as bcrypt from 'bcryptjs';

interface User {
  id: string;
  username: string;
  coins: number;
  items: any[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateCoins: (amount: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      loadUser(storedUserId);
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, coins, items')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setUser(data);
      } else {
        localStorage.removeItem('userId');
      }
    } catch (error) {
      console.error('Error loading user:', error);
      localStorage.removeItem('userId');
    } finally {
      setLoading(false);
    }
  };

  const register = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('username', username)
        .maybeSingle();

      if (existingUser) {
        return { success: false, error: 'Este nombre de usuario ya está en uso' };
      }

      const hashedPassword = await bcrypt.hash(password, 10);

      const { data, error } = await supabase
        .from('users')
        .insert([
          {
            username,
            password: hashedPassword,
            coins: 0,
            items: []
          }
        ])
        .select('id, username, coins, items')
        .single();

      if (error) throw error;

      setUser(data);
      localStorage.setItem('userId', data.id);
      return { success: true };
    } catch (error: any) {
      console.error('Error registering:', error);
      return { success: false, error: 'Error al crear la cuenta' };
    }
  };

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, password, coins, items')
        .eq('username', username)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        return { success: false, error: 'Usuario no encontrado' };
      }

      const passwordMatch = await bcrypt.compare(password, data.password);

      if (!passwordMatch) {
        return { success: false, error: 'Contraseña incorrecta' };
      }

      const userData = {
        id: data.id,
        username: data.username,
        coins: data.coins,
        items: data.items
      };

      setUser(userData);
      localStorage.setItem('userId', data.id);
      return { success: true };
    } catch (error: any) {
      console.error('Error logging in:', error);
      return { success: false, error: 'Error al iniciar sesión' };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('userId');
  };

  const updateCoins = async (amount: number) => {
    if (!user) return;

    const newCoins = user.coins + amount;
    
    const { error } = await supabase
      .from('users')
      .update({ coins: newCoins })
      .eq('id', user.id);

    if (!error) {
      setUser({ ...user, coins: newCoins });
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateCoins }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
